/*
 * TSS Skin Quiz, AI orchestration module
 * ------------------------------------------------------------
 * Purpose:
 *   Generate personalised copy (acknowledgement + per-product short/deep WHY)
 *   for the quiz result page. Acts as a pluggable layer so we can ship with
 *   hand-written templates today, then flip a switch to use a real AI
 *   backend (Cloudflare Worker calling Anthropic / OpenAI) later.
 *
 * Modes:
 *   - 'template' (default): uses TSSQuizCopy fallback strings. Instant, free.
 *   - 'api'     : POSTs to a configured endpoint, falls back to template on error.
 *
 * Caching:
 *   Generated copy is cached in localStorage by session_id so:
 *     - the AI is only called once per quiz completion
 *     - result persists across page reloads
 *     - the PDP reads from cache for instant render
 *
 * Events (emitted on document as CustomEvent 'tss-quiz:<name>'):
 *   - ai_generation_started      { session_id, mode }
 *   - ai_generation_success      { session_id, mode, latency_ms, source }
 *   - ai_generation_failure      { session_id, mode, latency_ms, error }
 *   - ai_cache_hit               { session_id }
 *   - ai_cache_miss              { session_id }
 *
 * Public API:
 *   TSSQuizAI.configure({ endpoint, mode, timeout_ms, version })
 *   TSSQuizAI.generate(state)    → Promise<{source, generated_at, ack, products}>
 *   TSSQuizAI.readCache(session_id)
 *   TSSQuizAI.clearCache(session_id)
 *
 * How to turn on real AI after deploying the backend:
 *   Paste this below the script tag for tss-quiz-ai.js (in theme.liquid or
 *   tss-quiz-widget.liquid), replacing the URL with your Worker URL:
 *
 *     <script>
 *       window.addEventListener('DOMContentLoaded', function () {
 *         if (window.TSSQuizAI) window.TSSQuizAI.configure({
 *           mode: 'api',
 *           endpoint: 'https://YOUR-WORKER-URL.workers.dev/generate'
 *         });
 *       });
 *     </script>
 */
(function () {
  'use strict';

  // ==========================================================================
  //  ★ EDIT THIS LINE TO TURN ON AI ★
  // --------------------------------------------------------------------------
  //  Paste your deployed Cloudflare Worker URL below (must start with https://
  //  and end with /generate). When this is set to a non-empty string, the
  //  quiz result page will call your Worker for personalised copy.
  //  Set it to '' (empty string) to fall back to template copy with no AI.
  //  Example:  var API_ENDPOINT = 'https://tss-quiz-ai.tsschatbot.workers.dev/generate';
  // ==========================================================================
  var API_ENDPOINT = 'https://tss-quiz-ai.tsschatbot.workers.dev/generate';
  // ==========================================================================

  var DEFAULTS = {
    mode: API_ENDPOINT ? 'api' : 'template',
    endpoint: API_ENDPOINT || null,
    // 25s client-side cap. Worker internally uses 20s for the Claude call,
    // so this gives ~5s of slack for network round-trip + JSON parse.
    timeout_ms: 25000,
    cache_prefix: 'tss_ai_v1_',
    version: 'v1'
  };
  var cfg = Object.assign({}, DEFAULTS);
  try { console.log('[TSS Quiz AI] loaded with:', { mode: cfg.mode, endpoint: cfg.endpoint }); } catch (e) {}

  function cacheKey(sid) { return cfg.cache_prefix + sid; }

  function readCache(sid) {
    try {
      var raw = localStorage.getItem(cacheKey(sid));
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writeCache(sid, data) {
    try { localStorage.setItem(cacheKey(sid), JSON.stringify(data)); } catch (e) {}
  }

  function clearCache(sid) {
    try { localStorage.removeItem(cacheKey(sid)); } catch (e) {}
  }

  function track(name, props) {
    try {
      document.dispatchEvent(new CustomEvent('tss-quiz:' + name, { detail: props || {} }));
    } catch (e) {}
    if (window.gtag) {
      try { window.gtag('event', 'tss_quiz_' + name, props || {}); } catch (e) {}
    }
    // Dev-visible trail so you can watch the AI flow in the browser console.
    // Comment out the next line if you ever want a quieter log.
    try { console.log('[TSS Quiz AI] ' + name, props || {}); } catch (e) {}
  }

  // ======================================================================
  // Template fallback, used when mode=template OR when API call fails.
  // Reads TSSQuizCopy entries and fills {skin} / {freqPhrase} tokens.
  // Must never throw, must always return a shape usable by the PDP renderer.
  // ======================================================================
  var SKIN_WORDS = { oily: 'oily', dry: 'dry', combination: 'combination', normal: 'normal', not_sure: 'combination' };
  var FREQ_PHRASES = {
    persistent: ', almost every day',
    frequent:   ', in waves every few weeks',
    periodic:   ', around your cycle',
    rare:       ', only occasionally'
  };

  function pickAckKey(buckets) {
    if (buckets.fungal_acne) return 'fungal';
    if (buckets.rosacea)     return 'rosacea';
    if (buckets.irritation)  return 'irritation';
    if (buckets.active_acne && buckets.marks)   return 'marks_acne';
    if (buckets.active_acne && buckets.texture) return 'texture_acne';
    if (buckets.active_acne) return 'active_acne';
    if (buckets.marks)       return 'marks';
    if (buckets.texture)     return 'texture';
    return 'maintenance';
  }

  function templateAck(persona, answers) {
    var C = window.TSSQuizCopy;
    if (!C || !C.output || !C.output.acknowledgement) return '';
    var tpls = C.output.acknowledgement.templates || {};
    var key = pickAckKey(persona.buckets || {});
    var tpl = tpls[key] || tpls.maintenance || '';
    var skinWord = SKIN_WORDS[answers.skin_type] || 'your';
    var freqPhrase = FREQ_PHRASES[persona.freq] || '';
    return tpl.replace('{skin}', skinWord).replace('{freqPhrase}', freqPhrase);
  }

  function pickWhyKey(code, persona) {
    var concerns = persona.why_concerns || [];
    var buckets  = persona.buckets || {};
    if (code === 'RCF') {
      if (persona.skin === 'dry') return 'dry';
      if (concerns.indexOf('marks') !== -1 && concerns.indexOf('active_acne') !== -1) return 'marks';
      if (concerns.indexOf('active_acne') === -1) return 'spot';
      return 'active_acne';
    }
    if (code === 'BSC') {
      if (buckets.irritation) return 'irritation';
      if (buckets.active_acne || buckets.marks || buckets.texture) return 'pair';
      return 'daily';
    }
    if (code === 'USDC') {
      if (persona.skin === 'dry') return 'texture_dry';
      if (concerns.indexOf('texture') !== -1) return 'texture';
      return 'maintenance';
    }
    if (code === 'SFARS') {
      if (buckets.fungal_acne || buckets.rosacea) return 'fungal_rosacea';
      if (concerns.indexOf('active_acne') !== -1 && concerns.indexOf('marks') !== -1) return 'active_acne';
      return 'marks';
    }
    if (code === 'SS') {
      if (buckets.irritation || buckets.rosacea) return 'sensitive';
      if (buckets.marks) return 'marks';
      if (buckets.active_acne || buckets.texture || buckets.fungal_acne) return 'active_use';
      return 'default';
    }
    return 'default';
  }

  function templateProductShort(code, persona) {
    var C = window.TSSQuizCopy;
    if (!C || !C.why_short) return '';
    var ws = C.why_short[code];
    if (!ws) return '';
    var key = pickWhyKey(code, persona);
    return ws[key] || ws['default'] || '';
  }

  function templateProductDeep(code, persona) {
    var C = window.TSSQuizCopy;
    if (!C || !C.why) return '';
    var wd = C.why[code];
    if (!wd) return '';
    var key = pickWhyKey(code, persona);
    return wd[key] || wd['default'] || '';
  }

  function templateGenerate(state) {
    var p = state.persona;
    var a = state.answers;
    var products = {};
    (p.product_meta || []).forEach(function (m) {
      products[m.code] = {
        short: templateProductShort(m.code, p),
        deep:  templateProductDeep(m.code, p)
      };
    });
    return {
      source: 'template',
      version: cfg.version,
      generated_at: new Date().toISOString(),
      session_id: state.session_id,
      persona_id: p.persona_id,
      ack: templateAck(p, a),
      products: products
    };
  }

  // ======================================================================
  // API call, POSTs to the configured endpoint with a timeout.
  // Endpoint must return JSON matching the shape returned by templateGenerate.
  // Any non-200 / network / timeout / parse error → falls back to template.
  // ======================================================================
  function apiGenerate(state) {
    if (!cfg.endpoint) return Promise.reject(new Error('No endpoint configured'));
    var ctrl = 'AbortController' in window ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, cfg.timeout_ms);

    return fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: state.session_id,
        answers: state.answers,
        persona: state.persona,
        client_version: cfg.version
      }),
      signal: ctrl && ctrl.signal
    }).then(function (res) {
      clearTimeout(timer);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(function (data) {
      if (!data || typeof data !== 'object' || !data.ack || !data.products) {
        throw new Error('Malformed response');
      }
      data.source = data.source || 'api';
      data.session_id = state.session_id;
      data.persona_id = state.persona.persona_id;
      data.generated_at = data.generated_at || new Date().toISOString();
      return data;
    });
  }

  // In-flight request de-duplication. Without this, both tss-quiz.js (on
  // quiz completion) and pdp.js (on result-page load) can fire their own
  // generate() call for the same session — doubling the cost. With this,
  // the second caller just awaits the first one's promise.
  var inflight = {};

  // ======================================================================
  // Main entry: generate (or fetch cached) copy for a completed quiz state.
  // Always resolves, never rejects. Falls back to templates on any failure.
  // ======================================================================
  function generate(state) {
    if (!state || !state.session_id || !state.persona) {
      return Promise.resolve(null);
    }
    var sid = state.session_id;

    var cached = readCache(sid);
    if (cached) {
      track('ai_cache_hit', { session_id: sid, source: cached.source });
      return Promise.resolve(cached);
    }

    if (inflight[sid]) {
      track('ai_inflight_reuse', { session_id: sid });
      return inflight[sid];
    }
    track('ai_cache_miss', { session_id: sid });

    if (cfg.mode !== 'api' || !cfg.endpoint) {
      var t = templateGenerate(state);
      writeCache(sid, t);
      track('ai_generation_success', { session_id: sid, mode: 'template', latency_ms: 0, source: 'template' });
      return Promise.resolve(t);
    }

    var start = Date.now();
    track('ai_generation_started', { session_id: sid, mode: 'api' });

    var p = apiGenerate(state).then(function (data) {
      writeCache(sid, data);
      track('ai_generation_success', {
        session_id: sid, mode: 'api',
        latency_ms: Date.now() - start,
        source: data.source
      });
      delete inflight[sid];
      return data;
    }).catch(function (err) {
      track('ai_generation_failure', {
        session_id: sid, mode: 'api',
        latency_ms: Date.now() - start,
        error: String(err && err.message ? err.message : err)
      });
      delete inflight[sid];
      var tmpl = templateGenerate(state);
      // Do NOT write to cache on failure, so a later retry can succeed.
      return tmpl;
    });
    inflight[sid] = p;
    return p;
  }

  window.TSSQuizAI = {
    configure: function (opts) {
      cfg = Object.assign({}, DEFAULTS, cfg, opts || {});
      try { console.log('[TSS Quiz AI] configured:', { mode: cfg.mode, endpoint: cfg.endpoint }); } catch (e) {}
    },
    generate:  generate,
    readCache: readCache,
    clearCache: clearCache,
    // Diagnostic: returns the live config so you can check mode/endpoint from the Console
    getConfig: function () { return Object.assign({}, cfg); },
    // Expose for testing / debugging
    _templateGenerate: templateGenerate
  };
})();
